/* ==========================================================================
   Cart Drawer — oddit-section-cart-drawer.js
   Shopify Cart AJAX API + Section Rendering API.
   All cart state is rendered server-side via Liquid; JS handles mutations,
   section re-fetching, drawer UI, and event delegation.
   ========================================================================== */

(function () {
  'use strict';

  // Shared body-scroll lock (reference-counted). Reuse the singleton created by
  // the header, or create it here if the cart drawer script loads first.
  window.OdditScrollLock =
    window.OdditScrollLock ||
    (function () {
      var locks = new Set();
      return {
        lock: function (id) {
          if (locks.size === 0) {
            var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.overflow = 'hidden';
            document.body.style.paddingRight = scrollbarWidth + 'px';
          }
          locks.add(id);
        },
        unlock: function (id) {
          locks.delete(id);
          if (locks.size === 0) {
            document.documentElement.style.overflow = '';
            document.body.style.paddingRight = '';
          }
        },
        isLocked: function () {
          return locks.size > 0;
        },
      };
    })();

  var SELECTORS = {
    drawer: '.oddit-cart-drawer',
    panel: '.oddit-cart-drawer__panel',
    overlay: '.oddit-cart-drawer__overlay',
    close: '.oddit-cart-drawer__close',
    recAdd: '.oddit-cart-drawer__rec-add[data-variant-id]',
    upsellCta: '.oddit-cart-drawer__upsell-cta[data-variant-id]',
    switchCta: '.oddit-cart-drawer__switch-cta',
    checkoutCta: '.oddit-cart-drawer__checkout-cta',
  };

  var CLASSES = { open: 'is-open' };
  var abortController = null;
  var lastRenderTime = 0;

  // ── Section Rendering ─────────────────────────────────────────────

  function getSectionId() {
    var drawer = document.querySelector(SELECTORS.drawer);
    return drawer ? drawer.getAttribute('data-section-id') : null;
  }

  function renderSectionHTML(html) {
    var drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    var wasOpen = drawer.classList.contains(CLASSES.open);

    // Preserve recommendations block if it has product cards (avoids blink on cart updates)
    var oldRecContainer = drawer.querySelector(
      '.oddit-cart-drawer__recommendations[data-cross-sell-source="product_recommendations"]',
    );
    var preservedRec = null;
    if (oldRecContainer) {
      var scroll = oldRecContainer.querySelector('.oddit-cart-drawer__recommendations-scroll');
      var hadContent = scroll && scroll.querySelector('.oddit-cart-drawer__rec-card');
      if (hadContent) {
        preservedRec = oldRecContainer.cloneNode(true);
      }
    }

    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var newDrawer = doc.querySelector(SELECTORS.drawer);

    if (newDrawer) {
      if (wasOpen) newDrawer.classList.add(CLASSES.open);

      if (preservedRec) {
        var newRecContainer = newDrawer.querySelector(
          '.oddit-cart-drawer__recommendations[data-cross-sell-source="product_recommendations"]',
        );
        if (newRecContainer) {
          newRecContainer.replaceWith(preservedRec);
        }
      }

      drawer.replaceWith(newDrawer);
      lastRenderTime = Date.now();
      bindCartEvents();
      loadRecommendations();
    }
  }

  async function fetchAndRenderSection() {
    var sectionId = getSectionId();
    if (!sectionId) return;
    var res = await fetch('/?sections=' + sectionId);
    var data = await res.json();
    if (data[sectionId]) {
      renderSectionHTML(data[sectionId]);
    }
  }

  function handleSectionResponse(data) {
    var sectionId = getSectionId();
    if (sectionId && data.sections && data.sections[sectionId]) {
      renderSectionHTML(data.sections[sectionId]);
      return true;
    }
    return false;
  }

  // ── Cart API ──────────────────────────────────────────────────────

  var HEADERS = { 'Content-Type': 'application/json' };

  function buildBody(obj) {
    var sectionId = getSectionId();
    if (sectionId) {
      obj.sections = [sectionId];
      obj.sections_url = window.location.pathname;
    }
    return JSON.stringify(obj);
  }

  async function addToCart(variantId, quantity, properties, sellingPlanId) {
    quantity = quantity || 1;
    properties = properties || {};
    var body = { id: variantId, quantity: quantity, properties: properties };
    if (sellingPlanId) body.selling_plan = sellingPlanId;
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: HEADERS,
      body: buildBody(body),
    });
    var data = await res.json();
    if (!res.ok) {
      await fetchAndRenderSection();
      dispatchCartEvent();
      throw new Error(data.description || data.message || 'Could not add item to cart');
    }
    if (!handleSectionResponse(data)) {
      await fetchAndRenderSection();
    }
    dispatchCartEvent();
    return data;
  }

  async function addMultipleToCart(items) {
    var body = { items: items };
    var sectionId = getSectionId();
    if (sectionId) {
      body.sections = [sectionId];
      body.sections_url = window.location.pathname;
    }
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(body),
    });
    var data = await res.json();
    if (!res.ok) {
      await fetchAndRenderSection();
      dispatchCartEvent();
      throw new Error(data.description || data.message || 'Could not add items to cart');
    }
    if (!handleSectionResponse(data)) {
      await fetchAndRenderSection();
    }
    dispatchCartEvent();
    return data;
  }

  async function updateLineItemQuantity(lineItemKey, quantity) {
    var res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: HEADERS,
      body: buildBody({ id: lineItemKey, quantity: quantity }),
    });
    var data = await res.json();
    if (!res.ok) {
      await fetchAndRenderSection();
      dispatchCartEvent();
      throw new Error(data.description || data.message || 'Could not update cart');
    }
    if (!handleSectionResponse(data)) {
      await fetchAndRenderSection();
    }
    dispatchCartEvent();
    return data;
  }

  async function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  async function updateLineItemToSubscription(lineItemKey, sellingPlanId, quantity) {
    var res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: HEADERS,
      body: buildBody({ id: lineItemKey, quantity: quantity || 1, selling_plan: sellingPlanId }),
    });
    var data = await res.json();
    if (!res.ok) {
      await fetchAndRenderSection();
      dispatchCartEvent();
      throw new Error(data.description || data.message || 'Could not update subscription');
    }
    if (!handleSectionResponse(data)) {
      await fetchAndRenderSection();
    }
    dispatchCartEvent();
    return data;
  }

  async function switchToOneTimePurchase(lineItemKey, variantId, quantity) {
    // Remove the subscription line item then re-add without a selling plan
    var removeRes = await fetch('/cart/change.js', {
      method: 'POST',
      headers: HEADERS,
      body: buildBody({ id: lineItemKey, quantity: 0 }),
    });
    if (!removeRes.ok) {
      var removeErr = await removeRes.json().catch(function () {
        return {};
      });
      await fetchAndRenderSection();
      dispatchCartEvent();
      throw new Error(removeErr.description || removeErr.message || 'Could not remove item');
    }
    var addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: HEADERS,
      body: buildBody({ id: variantId, quantity: quantity || 1 }),
    });
    if (!addRes.ok) {
      var addErr = await addRes.json().catch(function () {
        return {};
      });
      await fetchAndRenderSection();
      dispatchCartEvent();
      throw new Error(addErr.description || addErr.message || 'Could not re-add item');
    }
    await fetchAndRenderSection();
    dispatchCartEvent();
  }

  async function upgradeToBundle(variantId, replaceKeys, sellingPlanId) {
    var addPayload = { id: variantId, quantity: 1 };
    if (sellingPlanId) addPayload.selling_plan = sellingPlanId;
    var addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(addPayload),
    });

    if (!addRes.ok) {
      var err = await addRes.json().catch(function () {
        return {};
      });
      await fetchAndRenderSection();
      dispatchCartEvent();
      throw new Error(err.description || err.message || 'Could not add bundle to cart');
    }

    for (var i = 0; i < replaceKeys.length; i++) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: HEADERS,
        body: JSON.stringify({ id: replaceKeys[i], quantity: 0 }),
      });
    }

    await fetchAndRenderSection();
    dispatchCartEvent();
  }

  async function getCart() {
    var res = await fetch('/cart.js', { headers: HEADERS });
    return res.json();
  }

  async function refreshCart() {
    await fetchAndRenderSection();
    dispatchCartEvent();
  }

  function dispatchCartEvent() {
    getCart().then(function (cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    });
  }

  // ── Recommendations ──────────────────────────────────────────────

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML.replace(/"/g, '&quot;');
  }

  function sizedImageUrl(url, width) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function getMoneyFormat() {
    var drawer = document.querySelector(SELECTORS.drawer);
    return (drawer && drawer.getAttribute('data-money-format')) || '${{amount}}';
  }

  function formatMoney(cents) {
    var raw = (cents / 100).toFixed(2);
    var rawNoDecimals = Math.round(cents / 100).toString();

    function addSeparator(numStr, sep) {
      var parts = numStr.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return parts.join('.');
    }

    var amount = addSeparator(raw, ',');
    var amountNoDecimals = addSeparator(rawNoDecimals, ',');
    var amountWithComma = addSeparator(raw, '.').replace(/\.(\d+)$/, ',$1');
    var amountNoDecimalsWithComma = addSeparator(rawNoDecimals, '.');
    var amountWithApostrophe = addSeparator(raw, "'");
    var amountWithSpace = addSeparator(raw, ' ').replace('.', ',');
    var amountNoDecimalsWithSpace = addSeparator(rawNoDecimals, ' ');
    var amountWithPeriodAndSpace = addSeparator(raw, ' ');

    return getMoneyFormat()
      .replace('{{amount_with_apostrophe_separator}}', amountWithApostrophe)
      .replace('{{amount_no_decimals_with_space_separator}}', amountNoDecimalsWithSpace)
      .replace('{{amount_with_period_and_space_separator}}', amountWithPeriodAndSpace)
      .replace('{{amount_with_space_separator}}', amountWithSpace)
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  function buildRecCard(product) {
    var variant = null;
    if (product.variants && product.variants.length) {
      for (var v = 0; v < product.variants.length; v++) {
        if (product.variants[v].available) {
          variant = product.variants[v];
          break;
        }
      }
      if (!variant) variant = product.variants[0];
    }

    var variantId = variant ? variant.id : '';
    var price = product.price;
    var comparePrice = product.compare_at_price;
    var available = product.available !== false;

    var badgeHtml = '';
    if (comparePrice && comparePrice > price) {
      var savePct = Math.round(((comparePrice - price) / comparePrice) * 100);
      badgeHtml = '<div class="oddit-cart-drawer__rec-badge">Save ' + savePct + '%</div>';
    }

    var imageHtml = '';
    if (product.featured_image) {
      var imgUrl = sizedImageUrl(product.featured_image, 280);
      imageHtml =
        '<img class="oddit-cart-drawer__rec-image" src="' +
        imgUrl +
        '" alt="' +
        escapeHtml(product.title) +
        '" loading="lazy" width="140" draggable="false">';
    }

    var comparePriceHtml = '';
    if (comparePrice && comparePrice > price) {
      comparePriceHtml = '<span class="oddit-cart-drawer__rec-compare-price">' + formatMoney(comparePrice) + '</span>';
    }

    var btnHtml = '';
    if (available && variantId) {
      btnHtml =
        '<button class="oddit-cart-drawer__rec-add" type="button" data-product-id="' +
        product.id +
        '" data-variant-id="' +
        variantId +
        '">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">' +
        '<path fill-rule="evenodd" clip-rule="evenodd" d="M6.76822 1.66406V6.76822H1.66406V8.22655H6.76822V13.3307H8.22655V8.22655H13.3307V6.76822H8.22655V1.66406H6.76822Z" fill="currentColor"/>' +
        '</svg>' +
        'Add</button>';
    } else {
      btnHtml = '<button class="oddit-cart-drawer__rec-add" type="button" disabled>Sold out</button>';
    }

    return (
      '<div class="oddit-cart-drawer__rec-card" data-product-id="' +
      product.id +
      '">' +
      badgeHtml +
      '<div class="oddit-cart-drawer__rec-image-wrap">' +
      imageHtml +
      '</div>' +
      '<h4 class="oddit-cart-drawer__rec-name">' +
      escapeHtml(product.title) +
      '</h4>' +
      '<div class="oddit-cart-drawer__rec-pricing">' +
      '<span class="oddit-cart-drawer__rec-price">' +
      formatMoney(price) +
      '</span>' +
      comparePriceHtml +
      '</div>' +
      btnHtml +
      '</div>'
    );
  }

  async function loadRecommendations() {
    var container = document.querySelector(
      '.oddit-cart-drawer__recommendations[data-cross-sell-source="product_recommendations"]',
    );
    if (!container) return;

    var intent = container.getAttribute('data-recommendation-intent') || 'related';
    var limit = parseInt(container.getAttribute('data-max-products'), 10) || 6;
    var scroll = container.querySelector('.oddit-cart-drawer__recommendations-scroll');
    if (!scroll) return;

    var cart;
    try {
      cart = await getCart();
    } catch (e) {
      return;
    }
    if (!cart.items || cart.items.length === 0) {
      scroll.innerHTML = '';
      container.style.display = 'none';
      return;
    }

    var productId = cart.items[0].product_id;
    var cartProductIds = [];
    for (var c = 0; c < cart.items.length; c++) {
      cartProductIds.push(cart.items[c].product_id);
    }

    try {
      var res = await fetch(
        '/recommendations/products.json?product_id=' +
          productId +
          '&limit=' +
          (limit + cartProductIds.length) +
          '&intent=' +
          intent,
      );
      var data = await res.json();
      if (!data.products || data.products.length === 0) {
        scroll.innerHTML = '';
        container.style.display = 'none';
        return;
      }

      var html = '';
      var count = 0;
      for (var i = 0; i < data.products.length && count < limit; i++) {
        var product = data.products[i];
        if (cartProductIds.indexOf(product.id) !== -1) continue;
        html += buildRecCard(product);
        count++;
      }
      scroll.innerHTML = html;
      container.style.display = count > 0 ? '' : 'none';
    } catch (err) {
      // Silently fail — recommendations are non-critical
    }
  }

  // ── Drawer UI ─────────────────────────────────────────────────────

  function openDrawer(drawer) {
    if (!drawer) return;
    window.OdditScrollLock.lock('cart-drawer');
    drawer.classList.add(CLASSES.open);
    var closeBtn = drawer.querySelector(SELECTORS.close);
    if (closeBtn) {
      requestAnimationFrame(function () {
        closeBtn.focus();
      });
    }
    if (Date.now() - lastRenderTime > 2000) {
      fetchAndRenderSection();
    }
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove(CLASSES.open);
    window.OdditScrollLock.unlock('cart-drawer');
  }

  function trapFocus(e, drawer) {
    var panel = drawer.querySelector(SELECTORS.panel);
    if (!panel) return;
    var focusable = panel.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // ── Inline Error Feedback ─────────────────────────────────────────

  function showInlineError(anchorEl, message) {
    var existing = anchorEl.parentNode.querySelector('.oddit-cart-drawer__inline-error');
    if (existing) existing.remove();

    var notice = document.createElement('p');
    notice.className = 'oddit-cart-drawer__inline-error';
    notice.setAttribute('role', 'alert');
    notice.textContent = message;
    anchorEl.insertAdjacentElement('afterend', notice);

    setTimeout(function () {
      notice.classList.add('is-leaving');
      notice.addEventListener('transitionend', function () {
        notice.remove();
      });
    }, 4000);
  }

  // ── Recommendations Drag Scroll ───────────────────────────────────

  function setupDragScroll(el, signal) {
    var isDown = false;
    var startX;
    var startScrollLeft;

    function onMouseMove(e) {
      e.preventDefault();
      var walk = e.pageX - startX;
      el.scrollLeft = startScrollLeft - walk;
    }

    function onMouseUp() {
      if (!isDown) return;
      isDown = false;
      el.classList.remove('is-dragging');
      document.removeEventListener('mousemove', onMouseMove, { capture: true });
      document.removeEventListener('mouseup', onMouseUp, { capture: true });
    }

    function onMouseDown(e) {
      if (e.target.closest('button, a')) return;
      e.preventDefault();
      isDown = true;
      el.classList.add('is-dragging');
      startX = e.pageX;
      startScrollLeft = el.scrollLeft;
      document.addEventListener('mousemove', onMouseMove, { capture: true });
      document.addEventListener('mouseup', onMouseUp, { capture: true });
    }

    el.addEventListener('mousedown', onMouseDown, { signal: signal });
  }

  // ── Event Binding ─────────────────────────────────────────────────

  function bindCartEvents() {
    if (abortController) abortController.abort();
    abortController = new AbortController();
    var signal = abortController.signal;

    var drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    // Drawer chrome
    var overlay = drawer.querySelector(SELECTORS.overlay);
    var closeBtn = drawer.querySelector(SELECTORS.close);

    if (overlay) {
      overlay.addEventListener(
        'click',
        function () {
          closeDrawer(drawer);
        },
        { signal: signal },
      );
    }
    if (closeBtn) {
      closeBtn.addEventListener(
        'click',
        function () {
          closeDrawer(drawer);
        },
        { signal: signal },
      );
    }

    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'Escape' && drawer.classList.contains(CLASSES.open)) closeDrawer(drawer);
      },
      { signal: signal },
    );

    drawer.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'Tab' && drawer.classList.contains(CLASSES.open)) trapFocus(e, drawer);
      },
      { signal: signal },
    );

    // Upsell / cross-sell add buttons (delegated for dynamic cards)
    drawer.addEventListener(
      'click',
      function (e) {
        var addBtn = e.target.closest(SELECTORS.recAdd + ', ' + SELECTORS.upsellCta);
        if (!addBtn || addBtn.disabled) return;
        var variantId = parseInt(addBtn.getAttribute('data-variant-id'), 10);
        if (!variantId) return;
        addBtn.disabled = true;
        var originalHtml = addBtn.innerHTML;
        addBtn.classList.add('is-loading');
        addBtn.textContent = '';

        var replaceKeysAttr = addBtn.getAttribute('data-replace-keys');
        if (replaceKeysAttr && addBtn.matches(SELECTORS.upsellCta)) {
          var replaceKeys = replaceKeysAttr.split(',').filter(Boolean);
          var sellingPlanId = parseInt(addBtn.getAttribute('data-selling-plan-id'), 10) || null;
          upgradeToBundle(variantId, replaceKeys, sellingPlanId)
            .catch(function (err) {
              showInlineError(addBtn, err.message || 'Could not add bundle to cart');
            })
            .finally(function () {
              addBtn.classList.remove('is-loading');
              addBtn.innerHTML = originalHtml;
              addBtn.disabled = false;
            });
        } else {
          addToCart(variantId)
            .catch(function (err) {
              showInlineError(addBtn, err.message || 'Could not add item to cart');
            })
            .finally(function () {
              addBtn.classList.remove('is-loading');
              addBtn.innerHTML = originalHtml;
              addBtn.disabled = false;
            });
        }
      },
      { signal: signal },
    );

    // Quantity + remove via event delegation on the content area
    var content = drawer.querySelector('.oddit-cart-drawer__content');
    if (content) {
      content.addEventListener('click', handleContentClick, { signal: signal });
    }

    // Recommendations horizontal drag-to-scroll (desktop)
    var recScroll = drawer.querySelector('.oddit-cart-drawer__recommendations-scroll');
    if (recScroll) {
      setupDragScroll(recScroll, signal);
    }

    // Checkout
    var checkoutBtn = drawer.querySelector(SELECTORS.checkoutCta);
    if (checkoutBtn) {
      checkoutBtn.addEventListener(
        'click',
        function () {
          window.location.href = '/checkout';
        },
        { signal: signal },
      );
    }
  }

  async function handleContentClick(e) {
    // Edit Plan toggle
    var editBtn = e.target.closest('.oddit-cart-drawer__plan-edit-btn');
    if (editBtn) {
      var panelId = editBtn.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;
      var isExpanded = editBtn.getAttribute('aria-expanded') === 'true';
      panel.hidden = isExpanded;
      editBtn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      return;
    }

    // Plan Save — update selling plan on an existing subscription line item
    var saveBtn = e.target.closest('.oddit-cart-drawer__plan-save');
    if (saveBtn) {
      var key = saveBtn.getAttribute('data-line-key');
      var qty = parseInt(saveBtn.getAttribute('data-current-qty'), 10) || 1;
      var panel = saveBtn.closest('.oddit-cart-drawer__plan-panel');
      var select = panel ? panel.querySelector('.oddit-cart-drawer__plan-select') : null;
      if (!select || !key) return;
      var planId = parseInt(select.value, 10);
      saveBtn.disabled = true;
      try {
        if (planId === 0) {
          var variantId = parseInt(saveBtn.getAttribute('data-variant-id'), 10);
          if (!variantId) return;
          await switchToOneTimePurchase(key, variantId, qty);
        } else {
          await updateLineItemToSubscription(key, planId, qty);
        }
      } catch (err) {
        showInlineError(saveBtn, err.message || 'Could not update plan');
      } finally {
        saveBtn.disabled = false;
      }
      return;
    }

    // Plan Cancel — close the expanded panel
    var cancelBtn = e.target.closest('.oddit-cart-drawer__plan-cancel');
    if (cancelBtn) {
      var itemPlan = cancelBtn.closest('.oddit-cart-drawer__item-plan');
      if (!itemPlan) return;
      var panel = itemPlan.querySelector('.oddit-cart-drawer__plan-panel');
      var editBtn = itemPlan.querySelector('.oddit-cart-drawer__plan-edit-btn');
      if (panel) panel.hidden = true;
      if (editBtn) editBtn.setAttribute('aria-expanded', 'false');
      return;
    }

    // Switch to subscription — one-time item switching to a selling plan
    var switchBtn = e.target.closest('.oddit-cart-drawer__switch-cta') || e.target.closest('.oddit-cart-drawer__upgrade-cta');
    if (switchBtn) {
      var sellingPlanId = parseInt(switchBtn.getAttribute('data-selling-plan-id'), 10);
      var lineKey = switchBtn.getAttribute('data-line-key');
      var qty = parseInt(switchBtn.getAttribute('data-current-qty'), 10) || 1;
      if (!sellingPlanId || !lineKey) return;
      switchBtn.disabled = true;
      var originalText = switchBtn.textContent;
      switchBtn.classList.add('is-loading');
      switchBtn.textContent = '';
      try {
        await updateLineItemToSubscription(lineKey, sellingPlanId, qty);
      } catch (err) {
        showInlineError(switchBtn, err.message || 'Could not update subscription');
      } finally {
        switchBtn.classList.remove('is-loading');
        switchBtn.textContent = originalText;
        switchBtn.disabled = false;
      }
      return;
    }

    var qtyBtn = e.target.closest('[data-quantity-action]');
    if (qtyBtn) {
      var key = qtyBtn.getAttribute('data-line-key');
      var qty = parseInt(qtyBtn.getAttribute('data-current-qty'), 10) || 1;
      var action = qtyBtn.getAttribute('data-quantity-action');
      var newQty = action === 'increase' ? qty + 1 : Math.max(qty - 1, 0);
      qtyBtn.disabled = true;
      try {
        if (newQty === 0) {
          await removeFromCart(key);
        } else {
          await updateLineItemQuantity(key, newQty);
        }
      } catch (err) {
        showInlineError(qtyBtn, err.message || 'Could not update cart');
      } finally {
        qtyBtn.disabled = false;
      }
      return;
    }

    var removeBtn = e.target.closest('[data-remove-line]');
    if (removeBtn) {
      var rKey = removeBtn.getAttribute('data-line-key');
      removeBtn.disabled = true;
      try {
        await removeFromCart(rKey);
      } catch (err) {
        showInlineError(removeBtn, err.message || 'Could not remove item');
      } finally {
        removeBtn.disabled = false;
      }
    }
  }

  // ── Init / Destroy ────────────────────────────────────────────────

  function init() {
    bindCartEvents();
    loadRecommendations();
  }

  function destroy() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  // ── Public API ────────────────────────────────────────────────────

  window.OdditCartDrawer = {
    open: function () {
      openDrawer(document.querySelector(SELECTORS.drawer));
    },
    close: function () {
      closeDrawer(document.querySelector(SELECTORS.drawer));
    },
    toggle: function () {
      var d = document.querySelector(SELECTORS.drawer);
      if (!d) return;
      d.classList.contains(CLASSES.open) ? closeDrawer(d) : openDrawer(d);
    },
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
  };

  // ── Theme Editor ──────────────────────────────────────────────────

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector(SELECTORS.drawer)) init();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target.querySelector(SELECTORS.drawer)) destroy();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
